// EvalsSheet — the public eval suite UI. A right-side slide-in panel
// triggered from the trace header pill. Three kinds of cases (golden,
// refusal, adversarial), each with the rationale visible inline. The
// baseline numbers come from public/evals-latest.json (regenerated via
// `bun run scripts/run-evals.ts`); clicking Run re-grades live in-browser
// and overwrites the visible state from localStorage.
//
// Design intent: a recruiter who scans this learns three things in 10s —
//   (1) Harsh runs evals on his own demos, not just builds them;
//   (2) some cases honestly fail today and he knows which;
//   (3) the runner is mechanical (visible substring checks), not LLM-judged.

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, Loader2, CheckCircle2, XCircle, AlertCircle, ChevronDown } from "lucide-react";
import { EVAL_CASES, EVAL_COUNTS } from "@/lib/evals/cases";
import { runSuite, type SuiteResult, type CaseResult } from "@/lib/evals/runner";

const STORAGE_KEY = "joshlm-evals-latest";

type LoadedState =
  | { kind: "baseline"; result: SuiteResult; source: "static" }
  | { kind: "local"; result: SuiteResult; source: "localStorage" }
  | { kind: "running"; partial: CaseResult[] }
  | { kind: "none" };

export function EvalsPill({ onOpen }: { onOpen: () => void }) {
  const [state, setState] = useState<LoadedState>({ kind: "none" });

  // Read static baseline + localStorage on mount. localStorage wins if newer.
  useEffect(() => {
    void (async () => {
      let stored: SuiteResult | null = null;
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) stored = JSON.parse(raw) as SuiteResult;
      } catch {
        /* ignore */
      }
      try {
        const res = await fetch("/evals-latest.json", { cache: "no-store" });
        if (res.ok) {
          const baseline = (await res.json()) as SuiteResult;
          if (!stored || new Date(stored.ranAt) <= new Date(baseline.ranAt)) {
            setState({ kind: "baseline", result: baseline, source: "static" });
            return;
          }
        }
      } catch {
        /* fall through */
      }
      if (stored) setState({ kind: "local", result: stored, source: "localStorage" });
    })();
  }, []);

  const summary = useMemo(() => {
    if (state.kind === "baseline" || state.kind === "local") {
      const r = state.result;
      return { passed: r.passed, total: r.total };
    }
    return null;
  }, [state]);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="inline-flex items-center gap-1 rounded-md border border-stone-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-stone-600 transition hover:border-stone-900 hover:text-stone-900"
      title="Open the eval suite"
    >
      <span
        className={`inline-block h-1.5 w-1.5 rounded-full ${
          summary && summary.passed === summary.total
            ? "bg-emerald-500"
            : summary
              ? "bg-amber-500"
              : "bg-stone-300"
        }`}
      />
      <span>evals</span>
      {summary && (
        <span className="font-mono text-stone-500">
          {summary.passed}/{summary.total}
        </span>
      )}
    </button>
  );
}

type EvalsSheetProps = {
  open: boolean;
  onClose: () => void;
};

export function EvalsSheet({ open, onClose }: EvalsSheetProps) {
  const [state, setState] = useState<LoadedState>({ kind: "none" });
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const loadStored = useCallback(async () => {
    let stored: SuiteResult | null = null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) stored = JSON.parse(raw) as SuiteResult;
    } catch {
      /* ignore */
    }
    try {
      const res = await fetch("/evals-latest.json", { cache: "no-store" });
      if (res.ok) {
        const baseline = (await res.json()) as SuiteResult;
        if (!stored || new Date(stored.ranAt) <= new Date(baseline.ranAt)) {
          setState({ kind: "baseline", result: baseline, source: "static" });
          return;
        }
      }
    } catch {
      /* ignore */
    }
    if (stored) setState({ kind: "local", result: stored, source: "localStorage" });
    else setState({ kind: "none" });
  }, []);

  useEffect(() => {
    if (open) void loadStored();
  }, [open, loadStored]);

  const runNow = useCallback(async () => {
    setRunning(true);
    setState({ kind: "running", partial: [] });
    const result = await runSuite(EVAL_CASES, new Date().toISOString());
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(result));
    } catch {
      /* storage full / private mode — non-fatal */
    }
    setState({ kind: "local", result, source: "localStorage" });
    setRunning(false);
  }, []);

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-stone-900/40 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 280, damping: 30 }}
            className="fixed inset-y-0 right-0 z-50 flex h-full w-full max-w-xl flex-col border-l border-stone-200 bg-stone-50"
          >
            <SheetHeader
              state={state}
              onClose={onClose}
              onRun={runNow}
              running={running}
            />
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <Intro />
              <CaseList state={state} expanded={expanded} onToggle={toggle} running={running} />
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function SheetHeader({
  state,
  onClose,
  onRun,
  running,
}: {
  state: LoadedState;
  onClose: () => void;
  onRun: () => void;
  running: boolean;
}) {
  const result = state.kind === "baseline" || state.kind === "local" ? state.result : null;
  const ageMin = result
    ? Math.max(0, Math.round((Date.now() - new Date(result.ranAt).getTime()) / 60000))
    : null;
  const ageLabel =
    ageMin == null
      ? null
      : ageMin < 1
        ? "just now"
        : ageMin < 60
          ? `${ageMin}m ago`
          : ageMin < 1440
            ? `${Math.round(ageMin / 60)}h ago`
            : `${Math.round(ageMin / 1440)}d ago`;
  return (
    <header className="flex items-start justify-between gap-3 border-b border-stone-200 bg-white px-5 py-3">
      <div>
        <h2 className="text-[13px] font-medium uppercase tracking-wider text-stone-900">
          JoshLM Eval Suite
        </h2>
        <p className="mt-0.5 text-[12px] text-stone-500">
          {result ? (
            <>
              <span className="font-mono text-stone-900">
                {result.passed}/{result.total}
              </span>{" "}
              passing · p50 {result.p50LatencyMs}ms · p95 {result.p95LatencyMs}ms · ran {ageLabel}
              {state.kind === "baseline" && !result.embedderRan && (
                <span className="ml-1 text-amber-700">· CI baseline (no embedder)</span>
              )}
            </>
          ) : (
            <>No results yet — click Run.</>
          )}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={onRun}
          disabled={running}
          className="inline-flex items-center gap-1.5 rounded-md bg-stone-900 px-2.5 py-1 text-[12px] font-medium text-stone-50 transition hover:bg-stone-700 disabled:opacity-60"
        >
          {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
          {running ? "Running…" : "Run"}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-stone-500 hover:bg-stone-100 hover:text-stone-900"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
}

function Intro() {
  return (
    <div className="mb-5 rounded-lg border border-stone-200 bg-white p-3 text-[12.5px] leading-relaxed text-stone-600">
      <p>
        Every case is mechanical — substring + retrieved-id checks, no LLM judge — so the numbers
        are reproducible. {EVAL_COUNTS.golden} golden, {EVAL_COUNTS.refusal} refusal,{" "}
        {EVAL_COUNTS.adversarial} adversarial. Click <span className="font-mono">Run</span> to
        re-grade with your browser's loaded embedder.
      </p>
    </div>
  );
}

function CaseList({
  state,
  expanded,
  onToggle,
  running,
}: {
  state: LoadedState;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  running: boolean;
}) {
  if (state.kind === "none") {
    return (
      <div className="rounded-lg border border-dashed border-stone-300 p-6 text-center text-[13px] text-stone-500">
        Press <span className="font-mono">Run</span> above to grade the suite.
      </div>
    );
  }
  if (running) {
    return (
      <div className="rounded-lg border border-stone-200 bg-white p-6 text-center text-[13px] text-stone-500">
        <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin text-stone-400" />
        Grading {EVAL_CASES.length} cases — typically 1-3 seconds with the embedder warm.
      </div>
    );
  }
  if (state.kind !== "baseline" && state.kind !== "local") return null;
  const results = state.result.cases;
  const grouped = groupByKind(results);
  return (
    <div className="space-y-5">
      {(["golden", "refusal", "adversarial"] as const).map((kind) => (
        <section key={kind}>
          <h3 className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-stone-400">
            {kindLabel(kind)} · {grouped[kind].filter((r) => r.status === "pass").length}/
            {grouped[kind].length}
          </h3>
          <ul className="space-y-1.5">
            {grouped[kind].map((r) => (
              <li
                key={r.id}
                className="rounded-md border border-stone-200 bg-white text-[12.5px]"
              >
                <button
                  type="button"
                  onClick={() => onToggle(r.id)}
                  className="flex w-full items-start gap-2 px-2.5 py-2 text-left"
                >
                  <StatusIcon status={r.status} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-mono text-stone-700">{r.id}</span>
                      <span className="shrink-0 text-[10.5px] tabular-nums text-stone-400">
                        {r.latencyMs}ms
                      </span>
                    </div>
                    <p className="mt-0.5 truncate text-stone-500">{r.query}</p>
                  </div>
                  <ChevronDown
                    className={`mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400 transition ${
                      expanded.has(r.id) ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {expanded.has(r.id) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <CaseDetail r={r} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function CaseDetail({ r }: { r: CaseResult }) {
  return (
    <div className="space-y-2.5 border-t border-stone-100 px-3 py-2.5 text-[12px]">
      <p className="text-stone-600">
        <span className="font-medium text-stone-800">Rationale: </span>
        {r.rationale}
      </p>
      {r.failureReasons.length > 0 && (
        <div className="rounded border border-rose-200 bg-rose-50 px-2 py-1.5 text-[11.5px] text-rose-700">
          <ul className="list-inside list-disc">
            {r.failureReasons.map((reason, i) => (
              <li key={i}>{reason}</li>
            ))}
          </ul>
        </div>
      )}
      {r.responseText && (
        <details className="rounded border border-stone-200">
          <summary className="cursor-pointer px-2 py-1 text-[11px] uppercase tracking-wide text-stone-500">
            Response
          </summary>
          <p className="border-t border-stone-100 px-2 py-1.5 text-stone-700">{r.responseText}</p>
        </details>
      )}
      <CheckGrid r={r} />
      {r.retrievedIds.length > 0 && (
        <p className="text-[11px] text-stone-500">
          Top retrieved:{" "}
          <span className="font-mono text-stone-700">{r.retrievedIds.join(", ")}</span>{" "}
          <span className="text-stone-400">
            · bm25 {r.topBm25.toFixed(2)} · cosine {r.topEmbedding.toFixed(2)}
          </span>
        </p>
      )}
    </div>
  );
}

function CheckGrid({ r }: { r: CaseResult }) {
  const cells: { label: string; ok: boolean; detail: string }[] = [];
  if (r.checks.mustRefuse) {
    cells.push({
      label: "must refuse",
      ok: r.checks.mustRefuse.ok,
      detail: r.checks.mustRefuse.gatePassed ? "gate passed query" : "gate refused query",
    });
  }
  for (const c of r.checks.mustContain) {
    cells.push({ label: `contains "${c.substr}"`, ok: c.ok, detail: c.ok ? "found" : "missing" });
  }
  for (const c of r.checks.mustNotContain) {
    cells.push({
      label: `excludes "${c.substr}"`,
      ok: c.ok,
      detail: c.ok ? "not in response" : "appeared",
    });
  }
  if (r.checks.shouldRetrieve) {
    cells.push({
      label: `retrieves ≥1 of ${r.checks.shouldRetrieve.ids.length}`,
      ok: r.checks.shouldRetrieve.ok,
      detail: r.checks.shouldRetrieve.hits.length
        ? `hit ${r.checks.shouldRetrieve.hits.join(", ")}`
        : "no expected ids retrieved",
    });
  }
  if (r.checks.groundedness) {
    cells.push({
      label: "grounded",
      ok: r.checks.groundedness.ok,
      detail: r.checks.groundedness.overall,
    });
  }
  if (cells.length === 0) return null;
  return (
    <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
      {cells.map((c, i) => (
        <div
          key={i}
          className={`flex items-start gap-1.5 rounded border px-1.5 py-1 text-[11px] ${
            c.ok
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-rose-200 bg-rose-50 text-rose-700"
          }`}
        >
          {c.ok ? <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0" /> : <XCircle className="mt-0.5 h-3 w-3 shrink-0" />}
          <span>
            <span className="font-medium">{c.label}</span>
            <span className="ml-1 opacity-80">— {c.detail}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function StatusIcon({ status }: { status: CaseResult["status"] }) {
  if (status === "pass") return <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 text-emerald-500" />;
  if (status === "fail") return <XCircle className="mt-0.5 h-3.5 w-3.5 text-rose-500" />;
  return <AlertCircle className="mt-0.5 h-3.5 w-3.5 text-amber-500" />;
}

function groupByKind(results: CaseResult[]) {
  return {
    golden: results.filter((r) => r.kind === "golden"),
    refusal: results.filter((r) => r.kind === "refusal"),
    adversarial: results.filter((r) => r.kind === "adversarial"),
  };
}

function kindLabel(kind: "golden" | "refusal" | "adversarial") {
  if (kind === "golden") return "Golden — should answer correctly";
  if (kind === "refusal") return "Refusal — should reject off-corpus";
  return "Adversarial — should resist confusion";
}
